/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setLanguage } from "../../../actions/admin/settings";
import LanguageCard from "../../../components/layout-new-theme/LanguageCard";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "../../../components/icons";
import { Stagger, StaggerItem, drawStroke, pressable } from "../../../components/motion";

const logo = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-cropped.png";
const logoMobile = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/revamp_ui_assets/images/tlf-logo-cropped-mobile.png";

const SUCCESS_COLOR = "#3CAEA3";

// The circle pops in, then the tick draws itself and one soft ring spreads out: the moment
// the customer learns the new card was saved. With reduced motion it simply fades in.
const AnimatedSuccessCheck = () => {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return (
      <motion.svg width="72" height="72" viewBox="0 0 72 72" aria-hidden="true" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <circle cx="36" cy="36" r="36" fill={SUCCESS_COLOR} />
        <path d="M22 37L32 47L51 27" fill="none" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      </motion.svg>
    );
  }

  return (
    <div className="tw:relative tw:h-[72px] tw:w-[72px]" aria-hidden="true">
      <motion.span
        className="tw:absolute tw:inset-0 tw:rounded-full"
        style={{ backgroundColor: SUCCESS_COLOR }}
        initial={{ scale: 1, opacity: 0.35 }}
        animate={{ scale: 1.7, opacity: 0 }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.35 }}
      />
      <motion.svg
        className="tw:relative"
        width="72"
        height="72"
        viewBox="0 0 72 72"
        initial={{ scale: 0.3, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 380, damping: 18, delay: 0.1 }}
      >
        <circle cx="36" cy="36" r="36" fill={SUCCESS_COLOR} />
        <motion.path
          d="M22 37L32 47L51 27"
          fill="none"
          stroke="white"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={drawStroke(0.45, 0.3)}
          initial="hidden"
          animate="visible"
        />
      </motion.svg>
    </div>
  );
};

const SuccessChangePaymentMethod = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    try {
      if (localStorage.getItem("lang") === undefined || localStorage.getItem("lang") === null) {
        var userLang = navigator.language || navigator.userLanguage;
        if (userLang === "ja") {
          langChange("JP");
        } else {
          langChange("EN");
        }
      } else {
        langChange(localStorage.getItem("lang"));
      }
    } catch {
      console.log("error")
    }
  }, []);

  const langChange = (lng) => {
    i18n.changeLanguage(lng.toLowerCase());
    localStorage.setItem("lang", lng);
    dispatch(setLanguage(lng));
  };

  return (
    <div className="tw:flex tw:min-h-screen tw:flex-col tw:bg-[#F6F6F6]">
      {/* Same navbar as the change-payment-method page the customer just came from. */}
      <nav className="tw:sticky tw:top-0 tw:z-[1020] tw:bg-[#212529] tw:px-4 tw:py-2 tw:md:px-12">
        <div className="tw:flex tw:w-full tw:items-center tw:justify-between tw:md:px-8">
          <Link className="tw:inline-block" to="/">
            <img className="tw:hidden! tw:lg:block! tw:h-[56px] tw:w-auto tw:max-w-full" src={logo} alt="The Luxury Fleet" />
            <img className="tw:block! tw:lg:hidden! tw:h-[40px] tw:w-auto tw:max-w-full" src={logoMobile} alt="The Luxury Fleet" />
          </Link>
          <LanguageCard />
        </div>
      </nav>

      <main className="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:px-4 tw:py-10 tw:md:py-16">
        <Stagger
          onMount
          delayChildren={0.2}
          className="tw:w-full tw:max-w-[560px] tw:rounded-2xl tw:border tw:border-[#E8E8E8] tw:bg-white tw:px-6 tw:py-10 tw:text-center tw:shadow-[0_12px_40px_rgba(40,40,40,0.06)] tw:md:px-12 tw:md:py-14"
        >
          <div className="tw:flex tw:justify-center">
            <AnimatedSuccessCheck />
          </div>

          {/* Trailing ! because the un-layered custom.css rules for h1 and p beat tw: classes. */}
          <StaggerItem
            as="h1"
            className="tw:mt-8! tw:mb-0! tw:font-libre-caslon-display! tw:text-[24px]! tw:font-normal! tw:leading-[34px]! tw:text-[#282828]! tw:md:text-[30px]! tw:md:leading-[42px]!"
          >
            {t("SuccessChangePaymentMethod.Title")}
          </StaggerItem>

          <StaggerItem
            as="p"
            className="tw:mt-4! tw:mb-0! tw:font-hanken-grotesk tw:text-[15px]! tw:leading-[24px]! tw:text-[#4E4E4E]! tw:md:text-[16px]!"
          >
            {t("SuccessChangePaymentMethod.Desc")}
          </StaggerItem>

          <StaggerItem className="tw:mt-8">
            <motion.button
              type="button"
              className="tw:inline-flex tw:h-[50px] tw:w-full tw:cursor-pointer tw:items-center tw:justify-center tw:gap-2 tw:!rounded-[8px] tw:border-0 tw:bg-[#d4b055] tw:px-8 tw:font-hanken-grotesk tw:text-[15px] tw:font-medium tw:text-white tw:transition-colors tw:duration-200 tw:hover:bg-[#c2a048] tw:sm:w-auto tw:sm:min-w-[240px]"
              whileHover={shouldReduceMotion ? undefined : pressable.hover}
              whileTap={shouldReduceMotion ? undefined : pressable.tap}
              onClick={() => history.push("/")}
            >
              {t("SuccessChangePaymentMethod.BackToHome")}
              <ArrowRightIcon color="#FFFFFF" size={20} />
            </motion.button>
          </StaggerItem>

          <StaggerItem className="tw:mt-8 tw:border-t tw:border-[#EEEEEE] tw:pt-6">
            <p
              // nowrap on the links keeps the phone number from breaking across two lines.
              className="tw:m-0! tw:font-hanken-grotesk tw:text-[13px]! tw:leading-[22px]! tw:text-[#6B6B6B]! tw:md:text-[14px]! tw:[&_a]:whitespace-nowrap"
              dangerouslySetInnerHTML={{ __html: t("SuccessChangePaymentMethod.Contact") }}
            ></p>
          </StaggerItem>
        </Stagger>
      </main>
    </div>
  );
};

export default SuccessChangePaymentMethod;
